import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Play } from "lucide-react";
import { nextInterstitialAd } from "@/lib/adRotation";
import { PremiumAdCreative } from "@/components/ads/PremiumAdCreative";
import styles from "./InterstitialVideoAd.module.css";

const FORCED_VIEW_SECONDS = 3;

interface InterstitialVideoAdProps {
  onClose: () => void;
}

export function InterstitialVideoAd({ onClose }: InterstitialVideoAdProps) {
  const [ad] = useState(() => nextInterstitialAd());
  const [secondsLeft, setSecondsLeft] = useState(FORCED_VIEW_SECONDS);
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  useEffect(() => {
    // Tenta tocar com som automaticamente. Navegadores costumam bloquear áudio automático
    // quando não há um clique recente do usuário — se bloquear, mostramos um botão pra
    // tocar manualmente (esse clique aí sim garante o som).
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => setNeedsTapToPlay(true));
  }, []);

  const handleTapToPlay = () => {
    videoRef.current
      ?.play()
      .then(() => setNeedsTapToPlay(false))
      .catch(() => {});
  };

  if (!ad) return null;

  const canClose = secondsLeft <= 0;

  const isCreative = !ad.videoSrc && !ad.imageSrc;

  return (
    <div className={styles.backdrop}>
      <div className={styles.cardWrap}>
        <div className={styles.card}>
          <div className={styles.mediaArea}>
            {ad.videoSrc ? (
              <video ref={videoRef} className={styles.video} src={ad.videoSrc} poster={ad.posterSrc} playsInline />
            ) : ad.imageSrc ? (
              <img className={styles.video} src={ad.imageSrc} alt={ad.title} />
            ) : (
              <PremiumAdCreative variant="full" />
            )}

            {needsTapToPlay && ad.videoSrc && (
              <button type="button" className={styles.tapToPlay} onClick={handleTapToPlay}>
                <span className={styles.tapToPlayIcon}>
                  <Play size={22} strokeWidth={2} fill="currentColor" />
                </span>
                Toque para assistir com som
              </button>
            )}

            <span className={styles.sponsoredTag}>Publicidade</span>
          </div>
        </div>

        {canClose ? (
          <button type="button" className={styles.closeButton} aria-label="Fechar anúncio" onClick={onClose}>
            <X size={20} strokeWidth={2.5} />
          </button>
        ) : (
          <span className={styles.countdown}>{secondsLeft}</span>
        )}

        {isCreative && (
          <>
            <p className={styles.brandStraddle}>
              <span className={styles.brandText}>ControleNext</span>{" "}
              <span className={styles.brandStraddleAccent}>Premium</span>
            </p>
            <Link to={ad.href} onClick={onClose} className={styles.ctaExternal}>
              {ad.ctaLabel}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
